// Glossary of railway training and competence terms. Each entry has an id
// that internal FAQ answers link to (e.g. /glossary#ris-3751-tom).
export const GLOSSARY_TERMS = [
  {
    id: "ris-3751-tom",
    term: "RIS-3751-TOM",
    short: "The UK Rail Industry Standard defining the train driver selection psychometric assessment.",
    long:
      "RIS-3751-TOM sets out the cognitive and behavioural assessment requirements UK train operators use when selecting trainee train drivers. It covers attention, concentration, reaction speed, rule following and safety-critical judgement, and is the standard against which Train Driver Psychometrics aligns its practice resources.",
    related: ["psychometric", "nts", "operational-thinking"],
  },
  {
    id: "psychometric",
    term: "Psychometric assessment",
    short: "Structured tests measuring cognitive ability, attention, reaction and safety-critical judgement.",
    long:
      "Psychometric assessment refers to standardised cognitive and behavioural tests used during recruitment for safety-critical roles. For trainee train drivers in the UK, the battery is aligned to RIS-3751-TOM and covers areas like vigilance, reaction time, rule following and information-processing under pressure.",
    related: ["ris-3751-tom", "nts"],
  },
  {
    id: "nts",
    term: "Non-Technical Skills (NTS)",
    short: "Cognitive and social skills that underpin safe performance alongside technical knowledge.",
    long:
      "Non-Technical Skills (NTS) are the cognitive, social and personal-resource skills that complement technical knowledge in safety-critical roles. They include situational awareness, decision making, communication, teamwork, leadership and workload management. NTS are central to modern rail safety thinking.",
    related: ["human-factors", "operational-thinking", "safety-critical-communication"],
  },
  {
    id: "human-factors",
    term: "Human Factors (HF)",
    short: "The discipline of designing work, equipment and procedures around how people actually perform.",
    long:
      "Human Factors is the scientific discipline concerned with understanding interactions among humans and other elements of a system. In rail, applied HF helps design work, procedures, equipment, and safety culture to support reliable performance and reduce avoidable error.",
    related: ["nts", "fatigue", "incident-investigation"],
  },
  {
    id: "fatigue",
    term: "Fatigue and alertness",
    short: "The reduced state of mental and physical capability that degrades safe performance.",
    long:
      "Fatigue is a recognised safety risk in rail operations. It degrades attention, reaction time and decision making. Fatigue management combines fatigue science, rostering design, recovery strategies and organisational responsibilities — all of which apply to drivers, instructors, controllers and operational managers.",
    related: ["human-factors", "competence-management"],
  },
  {
    id: "operational-thinking",
    term: "Operational thinking",
    short: "The mindset of anticipating risk, interpreting rules and making sound real-time decisions.",
    long:
      "Operational thinking is the safety-critical mindset rail operators look for: reading a situation, anticipating risk, applying rules sensibly under pressure, communicating clearly and recovering after error. It is a strong predictor of trainee driver success.",
    related: ["nts", "risk-based-decision-making", "safety-critical-communication"],
  },
  {
    id: "risk-based-decision-making",
    term: "Risk-based decision making",
    short: "Choosing actions under uncertainty by weighing operational risk against operational pressure.",
    long:
      "Risk-based decision making is the structured way safety-critical staff weigh operational pressure against operational risk in real time. It is central to driver, instructor and manager development, and underpins how the industry responds to ambiguous or unexpected situations.",
    related: ["operational-thinking", "incident-investigation"],
  },
  {
    id: "safety-critical-communication",
    term: "Safety-critical communication",
    short: "Precise, challenge-tolerant communication used in operational rail contexts.",
    long:
      "Safety-critical communication is the precise, time-sensitive, challenge-tolerant communication used between drivers, signallers, controllers, dispatch and operations teams. It includes challenge-and-confirm habits, clear escalation, accurate handovers, and disciplined radio use.",
    related: ["nts", "operational-thinking"],
  },
  {
    id: "competence-management",
    term: "Competence management",
    short: "The structured system for developing, assessing and maintaining safety-critical capability.",
    long:
      "Competence management is the framework by which rail organisations develop, assess, evidence and maintain the safety-critical capability of their drivers, instructors and managers over time. Strong competence management is more than a tick-box — it shapes long-term safety performance.",
    related: ["incident-investigation", "nts", "human-factors"],
  },
  {
    id: "incident-investigation",
    term: "Incident investigation and learning",
    short: "Structured investigation focused on contributory factors and organisational learning.",
    long:
      "Incident investigation in rail goes beyond identifying immediate cause to surface contributory factors — including human factors, design, procedure and culture. Effective investigation supports organisational learning and a fair, just safety culture rather than blame.",
    related: ["human-factors", "competence-management", "risk-based-decision-making"],
  },
  {
    id: "future-driver",
    term: "Future Driver",
    short: "UKTDA's pathway preparing 18+ candidates for the recruitment journey under the new 2026 rules.",
    long:
      "Future Driver is the UKTDA development pathway for younger candidates preparing to apply when the minimum train driver age changes to 18 in Great Britain on 30 June 2026. It focuses on operational maturity, communication, rule discipline and psychometric readiness.",
    related: ["psychometric", "operational-thinking"],
  },
  {
    id: "cpd",
    term: "CPD (Continuing Professional Development)",
    short: "Ongoing professional learning supporting capability across a career.",
    long:
      "Continuing Professional Development (CPD) is structured, ongoing learning that supports professional capability across a career. In rail, CPD is particularly important for instructors, driver managers and operational leaders — capability changes as routes, traction and standards evolve.",
    related: ["competence-management", "nts"],
  },
];
